import React, { createContext, useState, useContext, ReactNode, useMemo } from 'react';

type Language = 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// English translations
const enTranslations: Record<string, string> = {
  'contact.phone': 'WhatsApp',
  'contact.github': 'GitHub',
  'contact.linkedin': 'LinkedIn',
  'contact.nameLabel': 'Name',
  'contact.emailLabel': 'Email',
  'contact.messageLabel': 'Message',
  'contact.submitButton': 'Send Message',
  'contact.sending': 'Sending...',
  'contact.successMessage': 'Success! I\'ll get back to you shortly.',
  'footer.role': 'Backend Engineer',
  'contact.nameRequired': 'Name is required',
  'contact.nameMinLength': 'Name must be at least 2 characters',
  'contact.emailRequired': 'Email is required',
  'contact.emailInvalid': 'Please enter a valid email address',
  'contact.messageRequired': 'Message is required',
  'contact.messageMinLength': 'Message must be at least 10 characters',
  'contact.errorMessage': 'Failed to send message. Please try again or contact me directly.',
};

// Portuguese translations
const ptTranslations: Record<string, string> = {
  'contact.phone': 'WhatsApp',
  'contact.github': 'GitHub',
  'contact.linkedin': 'LinkedIn',
  'contact.nameLabel': 'Nome',
  'contact.emailLabel': 'Email',
  'contact.messageLabel': 'Mensagem',
  'contact.submitButton': 'Enviar Mensagem',
  'contact.sending': 'Enviando...',
  'contact.successMessage': 'Sucesso! Retorno em breve.',
  'contact.errorMessage': 'Houve um erro ao enviar sua mensagem. Por favor, tente novamente.',
  'contact.nameRequired': 'Nome é obrigatório',
  'contact.nameMinLength': 'Nome deve ter pelo menos 2 caracteres',
  'contact.emailRequired': 'Email é obrigatório',
  'contact.emailInvalid': 'Por favor, insira um endereço de email válido',
  'contact.messageRequired': 'Mensagem é obrigatória',
  'contact.messageMinLength': 'Mensagem deve ter pelo menos 10 caracteres',
  'footer.role': 'Backend Engineer',
};

const translations: Record<Language, Record<string, string>> = {
  en: enTranslations,
  pt: ptTranslations,
};

// Helper function to get browser language
const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language.split('-')[0];
  return browserLang === 'pt' ? 'pt' : 'en';
};

// Helper function to get stored language
const getStoredLanguage = (): Language => {
  try {
    const stored = localStorage.getItem('portfolio-language');
    return (stored === 'pt' || stored === 'en') ? stored : getBrowserLanguage();
  } catch {
    return getBrowserLanguage();
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(getStoredLanguage);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  // Enhanced setLanguage function with localStorage persistence
  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    try {
      localStorage.setItem('portfolio-language', newLanguage);
    } catch {
      // Silently fail if localStorage is not available
    }
  };

  const contextValue = useMemo(() => ({
    language,
    setLanguage: handleSetLanguage,
    t
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
